import React, { useState, useCallback, useEffect, useRef } from 'react';
import { apiClient } from '../api/apiConfig';
import { Button, Alert, Spinner, Badge } from './ui';
import {
  PlusIcon,
  CheckCircleIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  TrashIcon,
  PencilIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

interface HealthGoal {
  id: number;
  name: string;
  description: string;
  goal_type: 'outcome' | 'process';
  goal_type_display: string;
  metric_type: string;
  metric_type_display: string;
  start_value: number | null;
  target_value: number;
  target_unit: string;
  frequency: string;
  frequency_display: string;
  comparison: string;
  comparison_display: string;
  start_date: string;
  end_date: string | null;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
  status_display: string;
  completed_at: string | null;
  category: number | null;
  category_name: string | null;
  progress_percentage: number;
  current_streak: number;
  created_at: string;
  updated_at: string;
}

interface GoalFormData {
  name: string;
  description: string;
  goal_type: 'outcome' | 'process';
  metric_type: string;
  start_value: string;
  target_value: string;
  target_unit: string;
  frequency: string;
  comparison: string;
  start_date: string;
  end_date: string;
}

const METRIC_TYPES = [
  // Daily process metrics
  { value: 'steps', label: 'Steps', unit: 'steps' },
  { value: 'sleep_hours', label: 'Sleep Duration', unit: 'hours' },
  { value: 'active_calories', label: 'Active Calories', unit: 'kcal' },
  { value: 'workout_minutes_excl_walking', label: 'Workout Minutes (Z2+)', unit: 'minutes' },
  // Outcome metrics
  { value: 'weight', label: 'Weight', unit: 'kg' },
  { value: 'body_fat', label: 'Body Fat', unit: '%' },
  { value: 'heart_rate_resting', label: 'Resting Heart Rate', unit: 'bpm' },
  { value: 'hrv', label: 'Heart Rate Variability', unit: 'ms' },
  { value: 'vo2_max', label: 'VO2 Max', unit: 'ml/kg/min' },
  { value: 'total_calories', label: 'Total Calories', unit: 'kcal' },
];

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const COMPARISONS = [
  { value: 'gte', label: 'At least (≥)' },
  { value: 'lte', label: 'At most (≤)' },
  { value: 'eq', label: 'Exactly (=)' },
];

const HealthGoals: React.FC = () => {
  const [goals, setGoals] = useState<HealthGoal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState<HealthGoal | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const formRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<GoalFormData>({
    name: '',
    description: '',
    goal_type: 'process',
    metric_type: 'steps',
    start_value: '',
    target_value: '',
    target_unit: 'steps',
    frequency: 'daily',
    comparison: 'gte',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: '',
  });

  const fetchGoals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const response = await apiClient.get('/api/health-goals/', { params });
      setGoals(response.data.results || response.data);
    } catch (err: any) {
      console.error('Error fetching goals:', err);
      setError(err.response?.data?.detail || 'Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const handleMetricTypeChange = (metricType: string) => {
    const metric = METRIC_TYPES.find((m) => m.value === metricType);
    setFormData({
      ...formData,
      metric_type: metricType,
      target_unit: metric?.unit || '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const payload = {
        ...formData,
        start_value: formData.start_value ? parseFloat(formData.start_value) : null,
        target_value: parseFloat(formData.target_value),
        end_date: formData.end_date || null,
      };

      if (editingGoal) {
        await apiClient.patch(`/api/health-goals/${editingGoal.id}/`, payload);
        setSuccess('Goal updated successfully!');
      } else {
        await apiClient.post('/api/health-goals/', payload);
        setSuccess('Goal created successfully!');
      }

      setShowForm(false);
      setEditingGoal(null);
      resetForm();
      fetchGoals();
    } catch (err: any) {
      console.error('Error saving goal:', err);
      setError(err.response?.data?.detail || 'Failed to save goal');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      goal_type: 'process',
      metric_type: 'steps',
      start_value: '',
      target_value: '',
      target_unit: 'steps',
      frequency: 'daily',
      comparison: 'gte',
      start_date: format(new Date(), 'yyyy-MM-dd'),
      end_date: '',
    });
  };

  const handleEdit = (goal: HealthGoal) => {
    setEditingGoal(goal);
    setFormData({
      name: goal.name,
      description: goal.description,
      goal_type: goal.goal_type || 'outcome',
      metric_type: goal.metric_type,
      start_value: goal.start_value?.toString() || '',
      target_value: goal.target_value.toString(),
      target_unit: goal.target_unit,
      frequency: goal.frequency,
      comparison: goal.comparison,
      start_date: goal.start_date,
      end_date: goal.end_date || '',
    });
    setShowForm(true);
    // Scroll to form after state update
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleDelete = async (goalId: number) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;

    try {
      setLoading(true);
      await apiClient.delete(`/api/health-goals/${goalId}/`);
      setSuccess('Goal deleted successfully!');
      fetchGoals();
    } catch (err: any) {
      console.error('Error deleting goal:', err);
      setError(err.response?.data?.detail || 'Failed to delete goal');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (goalId: number, action: 'mark_completed' | 'pause' | 'resume') => {
    try {
      setLoading(true);
      await apiClient.post(`/api/health-goals/${goalId}/${action}/`);
      setSuccess(`Goal ${action.replace('_', ' ')} successfully!`);
      fetchGoals();
    } catch (err: any) {
      console.error(`Error ${action} goal:`, err);
      setError(err.response?.data?.error || `Failed to ${action.replace('_', ' ')} goal`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'primary' | 'default'> = {
      active: 'success',
      paused: 'warning',
      completed: 'primary',
      abandoned: 'default',
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return COLORS.status.success;
    if (percentage >= 75) return COLORS.accent.default;
    if (percentage >= 50) return COLORS.status.warning;
    return COLORS.status.error;
  };

  if (loading && goals.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: SPACING[48] }}>
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{
            fontSize: TYPOGRAPHY.headings.sizes.h1,
            fontWeight: TYPOGRAPHY.weight.bold,
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            color: COLORS.text.primary,
            margin: 0,
          }}>
            Health Goals
          </h2>
          <p style={{
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.secondary,
            marginTop: SPACING[4],
            margin: 0,
            marginBlockStart: SPACING[4],
          }}>
            Set and track your health targets
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={<PlusIcon style={{ height: '20px', width: '20px' }} />}
          onClick={() => {
            setShowForm(true);
            setEditingGoal(null);
            resetForm();
          }}
        >
          New Goal
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', gap: SPACING[8] }}>
        {['all', 'active', 'paused', 'completed'].map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            style={{
              padding: `${SPACING[8]} ${SPACING[16]}`,
              borderRadius: RADIUS,
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: TYPOGRAPHY.weight.medium,
              transition: 'background-color 0.2s, color 0.2s',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: filterStatus === status ? COLORS.accent.default : COLORS.bg.hover,
              color: filterStatus === status ? '#FFFFFF' : COLORS.text.secondary,
            }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Goal Form */}
      {showForm && (
        <div
          ref={formRef}
          style={{
            backgroundColor: COLORS.bg.panel,
            borderRadius: RADIUS,
            padding: SPACING[24],
            border: `1px solid ${COLORS.border.default}`,
          }}
        >
          <h3 style={{
            fontSize: TYPOGRAPHY.headings.sizes.h2,
            fontWeight: TYPOGRAPHY.weight.semibold,
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            color: COLORS.text.primary,
            marginBottom: SPACING[16],
            margin: 0,
            marginBlockEnd: SPACING[16],
          }}>
            {editingGoal ? 'Edit Goal' : 'Create New Goal'}
          </h3>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(1, 1fr)',
              gap: SPACING[16],
            }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.size.sm,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[4],
                }}>
                  Goal Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: `${SPACING[8]} ${SPACING[12]}`,
                    border: `1px solid ${COLORS.border.default}`,
                    borderRadius: RADIUS,
                    fontSize: TYPOGRAPHY.size.base,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  placeholder="e.g., Walk 10,000 steps daily"
                  required
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.size.sm,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[4],
                }}>
                  Goal Type
                </label>
                <div style={{ display: 'flex', gap: SPACING[8] }}>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, goal_type: 'process' })}
                    style={{
                      flex: 1,
                      padding: `${SPACING[8]} ${SPACING[12]}`,
                      border: `2px solid ${formData.goal_type === 'process' ? COLORS.accent.default : COLORS.border.default}`,
                      borderRadius: RADIUS,
                      backgroundColor: formData.goal_type === 'process' ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{
                      fontSize: TYPOGRAPHY.size.sm,
                      fontWeight: TYPOGRAPHY.weight.semibold,
                      color: formData.goal_type === 'process' ? COLORS.accent.default : COLORS.text.primary,
                    }}>
                      Process
                    </div>
                    <div style={{
                      fontSize: TYPOGRAPHY.size.xs,
                      color: COLORS.text.secondary,
                      marginTop: SPACING[4],
                    }}>
                      Track consistent behaviors (steps, sleep, workouts)
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, goal_type: 'outcome' })}
                    style={{
                      flex: 1,
                      padding: `${SPACING[8]} ${SPACING[12]}`,
                      border: `2px solid ${formData.goal_type === 'outcome' ? COLORS.accent.default : COLORS.border.default}`,
                      borderRadius: RADIUS,
                      backgroundColor: formData.goal_type === 'outcome' ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{
                      fontSize: TYPOGRAPHY.size.sm,
                      fontWeight: TYPOGRAPHY.weight.semibold,
                      color: formData.goal_type === 'outcome' ? COLORS.accent.default : COLORS.text.primary,
                    }}>
                      Outcome
                    </div>
                    <div style={{
                      fontSize: TYPOGRAPHY.size.xs,
                      color: COLORS.text.secondary,
                      marginTop: SPACING[4],
                    }}>
                      Target a specific result (weight, VO2max)
                    </div>
                  </button>
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.size.sm,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[4],
                }}>
                  Metric Type
                </label>
                <select
                  value={formData.metric_type}
                  onChange={(e) => handleMetricTypeChange(e.target.value)}
                  style={{
                    width: '100%',
                    padding: `${SPACING[8]} ${SPACING[12]}`,
                    border: `1px solid ${COLORS.border.default}`,
                    borderRadius: RADIUS,
                    fontSize: TYPOGRAPHY.size.base,
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: COLORS.bg.panel,
                  }}
                  required
                >
                  {METRIC_TYPES.map((metric) => (
                    <option key={metric.value} value={metric.value}>
                      {metric.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Start Value - only for outcome goals */}
              {formData.goal_type === 'outcome' && (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: TYPOGRAPHY.size.sm,
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.secondary,
                    marginBottom: SPACING[4],
                  }}>
                    Start Value (for progress tracking)
                  </label>
                  <div style={{ display: 'flex', gap: SPACING[8] }}>
                    <input
                      type="number"
                      step="any"
                      value={formData.start_value}
                      onChange={(e) => setFormData({ ...formData, start_value: e.target.value })}
                      style={{
                        flex: 1,
                        padding: `${SPACING[8]} ${SPACING[12]}`,
                        border: `1px solid ${COLORS.border.default}`,
                        borderRadius: RADIUS,
                        fontSize: TYPOGRAPHY.size.base,
                        outline: 'none',
                      }}
                      placeholder="Your current value"
                    />
                    <span style={{
                      padding: `${SPACING[8]} ${SPACING[12]}`,
                      backgroundColor: COLORS.bg.hover,
                      border: `1px solid ${COLORS.border.default}`,
                      borderRadius: RADIUS,
                      color: COLORS.text.secondary,
                      fontSize: TYPOGRAPHY.size.sm,
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {formData.target_unit}
                    </span>
                  </div>
                </div>
              )}

              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.size.sm,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[4],
                }}>
                  Target Value
                </label>
                <div style={{ display: 'flex', gap: SPACING[8] }}>
                  <select
                    value={formData.comparison}
                    onChange={(e) => setFormData({ ...formData, comparison: e.target.value })}
                    style={{
                      width: '128px',
                      padding: `${SPACING[8]} ${SPACING[12]}`,
                      border: `1px solid ${COLORS.border.default}`,
                      borderRadius: RADIUS,
                      fontSize: TYPOGRAPHY.size.base,
                      outline: 'none',
                      backgroundColor: COLORS.bg.panel,
                    }}
                    required
                  >
                    {COMPARISONS.map((comp) => (
                      <option key={comp.value} value={comp.value}>
                        {comp.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="any"
                    value={formData.target_value}
                    onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                    style={{
                      flex: 1,
                      padding: `${SPACING[8]} ${SPACING[12]}`,
                      border: `1px solid ${COLORS.border.default}`,
                      borderRadius: RADIUS,
                      fontSize: TYPOGRAPHY.size.base,
                      outline: 'none',
                    }}
                    placeholder="Target"
                    required
                  />
                  <span style={{
                    padding: `${SPACING[8]} ${SPACING[12]}`,
                    backgroundColor: COLORS.bg.hover,
                    border: `1px solid ${COLORS.border.default}`,
                    borderRadius: RADIUS,
                    color: COLORS.text.secondary,
                    fontSize: TYPOGRAPHY.size.sm,
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    {formData.target_unit}
                  </span>
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.size.sm,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[4],
                }}>
                  Frequency
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  style={{
                    width: '100%',
                    padding: `${SPACING[8]} ${SPACING[12]}`,
                    border: `1px solid ${COLORS.border.default}`,
                    borderRadius: RADIUS,
                    fontSize: TYPOGRAPHY.size.base,
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: COLORS.bg.panel,
                  }}
                  required
                >
                  {FREQUENCIES.map((freq) => (
                    <option key={freq.value} value={freq.value}>
                      {freq.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.size.sm,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[4],
                }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: `${SPACING[8]} ${SPACING[12]}`,
                    border: `1px solid ${COLORS.border.default}`,
                    borderRadius: RADIUS,
                    fontSize: TYPOGRAPHY.size.base,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  required
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: TYPOGRAPHY.size.sm,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary,
                  marginBottom: SPACING[4],
                }}>
                  End Date (Optional)
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  style={{
                    width: '100%',
                    padding: `${SPACING[8]} ${SPACING[12]}`,
                    border: `1px solid ${COLORS.border.default}`,
                    borderRadius: RADIUS,
                    fontSize: TYPOGRAPHY.size.base,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: TYPOGRAPHY.size.sm,
                fontWeight: TYPOGRAPHY.weight.medium,
                color: COLORS.text.secondary,
                marginBottom: SPACING[4],
              }}>
                Description (Optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                style={{
                  width: '100%',
                  padding: `${SPACING[8]} ${SPACING[12]}`,
                  border: `1px solid ${COLORS.border.default}`,
                  borderRadius: RADIUS,
                  fontSize: TYPOGRAPHY.size.base,
                  outline: 'none',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                }}
                rows={3}
                placeholder="Why is this goal important to you?"
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: SPACING[12] }}>
              <Button
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setEditingGoal(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" isLoading={loading}>
                {editingGoal ? 'Update Goal' : 'Create Goal'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Goals List */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(1, 1fr)',
        gap: SPACING[24],
      }}>
        {goals.map((goal) => (
          <div
            key={goal.id}
            style={{
              backgroundColor: COLORS.bg.panel,
              borderRadius: RADIUS,
              padding: SPACING[24],
              border: `1px solid ${COLORS.border.default}`,
              transition: 'border-color 0.2s',
            }}
          >
            {/* Goal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: SPACING[16] }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8], marginBottom: SPACING[4] }}>
                  <h3 style={{
                    fontSize: TYPOGRAPHY.headings.sizes.h2,
                    fontWeight: TYPOGRAPHY.weight.semibold,
                    fontFamily: TYPOGRAPHY.headings.fontFamily,
                    color: COLORS.text.primary,
                    margin: 0,
                  }}>
                    {goal.name}
                  </h3>
                  <Badge variant={goal.goal_type === 'outcome' ? 'primary' : 'success'}>
                    {goal.goal_type_display}
                  </Badge>
                </div>
                <p style={{
                  fontSize: TYPOGRAPHY.size.sm,
                  color: COLORS.text.secondary,
                  margin: 0,
                }}>
                  {goal.metric_type_display}
                </p>
              </div>
              {getStatusBadge(goal.status)}
            </div>

            {/* Description */}
            {goal.description && (
              <p style={{
                fontSize: TYPOGRAPHY.size.sm,
                color: COLORS.text.secondary,
                marginBottom: SPACING[16],
                margin: 0,
                marginBlockEnd: SPACING[16],
              }}>
                {goal.description}
              </p>
            )}

            {/* Target Info */}
            <div style={{
              backgroundColor: COLORS.bg.base,
              borderRadius: RADIUS,
              padding: SPACING[12],
              marginBottom: SPACING[16],
              display: 'flex',
              flexDirection: 'column',
              gap: SPACING[8],
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: TYPOGRAPHY.size.sm }}>
                <span style={{ color: COLORS.text.secondary }}>Target:</span>
                <span style={{ fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary }}>
                  {goal.comparison_display} {goal.target_value} {goal.target_unit}
                </span>
              </div>
              {goal.goal_type === 'process' && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: TYPOGRAPHY.size.sm }}>
                    <span style={{ color: COLORS.text.secondary }}>Frequency:</span>
                    <span style={{ fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary }}>{goal.frequency_display}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: TYPOGRAPHY.size.sm }}>
                    <span style={{ color: COLORS.text.secondary }}>Streak:</span>
                    <span style={{ fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary }}>{goal.current_streak} days</span>
                  </div>
                </>
              )}
            </div>

            {/* Progress Bar */}
            <div style={{ marginBottom: SPACING[16] }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: TYPOGRAPHY.size.sm, marginBottom: SPACING[8] }}>
                <span style={{ color: COLORS.text.secondary }}>
                  {goal.goal_type === 'outcome' ? 'Progress to target' : 'Recent achievement'}
                </span>
                <span style={{ fontWeight: TYPOGRAPHY.weight.semibold, color: COLORS.text.primary }}>{goal.progress_percentage}%</span>
              </div>
              <div style={{
                width: '100%',
                backgroundColor: COLORS.border.default,
                borderRadius: RADIUS,
                height: '8px',
              }}>
                <div
                  style={{
                    height: '8px',
                    borderRadius: RADIUS,
                    transition: 'width 0.3s',
                    backgroundColor: getProgressColor(goal.progress_percentage),
                    width: `${Math.min(goal.progress_percentage, 100)}%`,
                  }}
                />
              </div>
            </div>

            {/* Dates */}
            <div style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.tertiary,
              marginBottom: SPACING[16],
            }}>
              <div>Started: {format(parseISO(goal.start_date), 'MMM d, yyyy')}</div>
              {goal.end_date && <div>Ends: {format(parseISO(goal.end_date), 'MMM d, yyyy')}</div>}
              {goal.completed_at && (
                <div>Completed: {format(parseISO(goal.completed_at), 'MMM d, yyyy')}</div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING[8] }}>
              {goal.status === 'active' && (
                <>
                  <button
                    onClick={() => handleStatusChange(goal.id, 'mark_completed')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING[4],
                      padding: `${SPACING[4]} ${SPACING[12]}`,
                      backgroundColor: '#D1FAE5',
                      color: '#047857',
                      borderRadius: RADIUS,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: TYPOGRAPHY.size.sm,
                      transition: 'background-color 0.2s',
                    }}
                    title="Mark as completed"
                  >
                    <CheckCircleIcon style={{ height: '16px', width: '16px' }} />
                    <span>Complete</span>
                  </button>
                  <button
                    onClick={() => handleStatusChange(goal.id, 'pause')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING[4],
                      padding: `${SPACING[4]} ${SPACING[12]}`,
                      backgroundColor: '#FEF3C7',
                      color: '#B45309',
                      borderRadius: RADIUS,
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: TYPOGRAPHY.size.sm,
                      transition: 'background-color 0.2s',
                    }}
                    title="Pause goal"
                  >
                    <PauseCircleIcon style={{ height: '16px', width: '16px' }} />
                    <span>Pause</span>
                  </button>
                </>
              )}
              {goal.status === 'paused' && (
                <button
                  onClick={() => handleStatusChange(goal.id, 'resume')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: SPACING[4],
                    padding: `${SPACING[4]} ${SPACING[12]}`,
                    backgroundColor: '#DBEAFE',
                    color: '#1D4ED8',
                    borderRadius: RADIUS,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: TYPOGRAPHY.size.sm,
                    transition: 'background-color 0.2s',
                  }}
                  title="Resume goal"
                >
                  <PlayCircleIcon style={{ height: '16px', width: '16px' }} />
                  <span>Resume</span>
                </button>
              )}
              <button
                onClick={() => handleEdit(goal)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING[4],
                  padding: `${SPACING[4]} ${SPACING[12]}`,
                  backgroundColor: COLORS.bg.hover,
                  color: COLORS.text.secondary,
                  borderRadius: RADIUS,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: TYPOGRAPHY.size.sm,
                  transition: 'background-color 0.2s',
                }}
                title="Edit goal"
              >
                <PencilIcon style={{ height: '16px', width: '16px' }} />
                <span>Edit</span>
              </button>
              <button
                onClick={() => handleDelete(goal.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING[4],
                  padding: `${SPACING[4]} ${SPACING[12]}`,
                  backgroundColor: '#FEE2E2',
                  color: '#B91C1C',
                  borderRadius: RADIUS,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: TYPOGRAPHY.size.sm,
                  transition: 'background-color 0.2s',
                }}
                title="Delete goal"
              >
                <TrashIcon style={{ height: '16px', width: '16px' }} />
                <span>Delete</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {!loading && goals.length === 0 && (
        <div style={{ textAlign: 'center', padding: SPACING[48] }}>
          <ChartBarIcon style={{ margin: '0 auto', height: '48px', width: '48px', color: COLORS.text.tertiary }} />
          <h3 style={{
            marginTop: SPACING[8],
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.medium,
            color: COLORS.text.primary,
          }}>
            No goals found
          </h3>
          <p style={{
            marginTop: SPACING[4],
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.tertiary,
          }}>
            {filterStatus === 'all'
              ? 'Get started by creating your first health goal.'
              : `No ${filterStatus} goals found. Try changing the filter.`}
          </p>
        </div>
      )}
    </div>
  );
};

export default HealthGoals;
